// Servers page — console window.
//  RCON tab: send commands to any server (history with ↑ / ↓).
//  Server log tab (LinuxGSM servers only): the console log over one SSH session - the last 500
//  lines, then every new line live (GET …/linuxgsm/console/stream). A LinuxGSM action started from
//  the row menu streams its output into the same view (POST …/linuxgsm/run).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { SvIcon } from './svIcons';
import { shownAddress } from './ServerTable';
import { sseFetch } from '../../utils/sseFetch';

const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const HISTORY_LINES = 500;
const MAX_LINES = 5000; // kept in the view; older lines drop off the top
// one-click RCON commands (read-only: each only prints a value)
const QUICK_COMMANDS = ['status', 'hostname', 'rcon_password', 'tv_status', 'tv_password', 'tv_password1', 'logaddress_list_http'];

export default function ServerConsole({ server, lgsmAction, onClose, onDone }) {
  const { t } = useTranslation();
  const hasLog = !!server.linuxgsm_enabled && !!server.has_backup;
  const [tab, setTab] = useState(lgsmAction ? 'log' : 'rcon');

  // RCON
  const [lines, setLines] = useState([]);
  const [cmd, setCmd] = useState('');
  const [history, setHistory] = useState([]);
  const [hIdx, setHIdx] = useState(-1);
  const [sending, setSending] = useState(false);
  // Server log (LinuxGSM)
  const [log, setLog] = useState([]); // { text, kind: log | act | sep | ok | warn | err | dim }
  const [stream, setStream] = useState('off'); // off | connecting | live | ended | error
  const [logFile, setLogFile] = useState('');
  const [running, setRunning] = useState(false);
  const streamAbort = useRef(null);
  const actionAbort = useRef(null);

  const outRef = useRef(null);
  const inputRef = useRef(null);
  const stick = useRef(true); // follow new lines while the view is scrolled to the bottom

  useEffect(() => { if (outRef.current && stick.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [lines, log, tab]);
  const onOutScroll = () => {
    const el = outRef.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const addLog = useCallback((items) => {
    setLog(l => { const next = l.concat(items); return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next; });
  }, []);

  // Live console: the last 500 lines, then new ones as the server writes them. Reconnecting starts
  // over (fresh 500 lines), so nothing is shown twice.
  const startStream = useCallback(() => {
    if (streamAbort.current) streamAbort.current.abort();
    const ctl = new AbortController();
    streamAbort.current = ctl;
    setLog(l => l.filter(x => x.kind !== 'log' && x.kind !== 'dim'));
    setLogFile('');
    setStream('connecting');
    sseFetch(`/api/servers/${server.id}/linuxgsm/console/stream?lines=${HISTORY_LINES}`, {
      signal: ctl.signal,
      onEvent: (event, data) => {
        if (event === 'file') { setLogFile(data.path); setStream('live'); }
        else if (event === 'line') addLog({ text: data.text, kind: 'log' });
        else if (event === 'nolog') { addLog({ text: t('servers.consoleNoLog', 'No console log found. Checked: {{paths}}', { paths: (data.checked || []).join(', ') }), kind: 'dim' }); setStream('ended'); }
        else if (event === 'error') { addLog({ text: data.error, kind: 'err' }); setStream('error'); }
      },
    }).then(() => {
      if (!ctl.signal.aborted) setStream(s => (s === 'error' ? s : 'ended'));
    }).catch((err) => {
      if (ctl.signal.aborted) return;
      addLog({ text: err.message || t('servers.consoleLogFailed', 'Failed to fetch the console log'), kind: 'err' });
      setStream('error');
    });
  }, [server.id, addLog, t]);

  const stopStream = () => {
    if (streamAbort.current) streamAbort.current.abort();
    streamAbort.current = null;
    setStream('off');
  };

  // LinuxGSM action: output streamed into the same view. Closing the window doesn't stop it on the server.
  const runAction = useCallback(async (action) => {
    const ctl = new AbortController();
    actionAbort.current = ctl;
    setRunning(true);
    const script = server.linuxgsm_script || 'cs2server';
    addLog({ text: `━━━━━━━━ [${now()}] ./${script.split('/').pop()} ${action} ━━━━━━━━`, kind: 'sep' });
    try {
      let result = null;
      await sseFetch(`/api/servers/${server.id}/linuxgsm/run`, {
        method: 'POST', body: { action }, signal: ctl.signal,
        onEvent: (event, data) => {
          if (event === 'line') addLog({ text: data.text, kind: 'act' });
          else if (event === 'done' || event === 'error') result = { event, ...data };
        },
      });
      if (!result) addLog({ text: `✖ [${now()}] ${t('servers.lgsmNoResult', 'The connection ended before the command finished - it may still be running on the server.')}`, kind: 'err' });
      else if (result.event === 'error') addLog({ text: `✖ [${now()}] ${result.error}`, kind: 'err' });
      else if (result.timedOut) addLog({ text: `✖ [${now()}] ${t('servers.lgsmTimedOut', '{{action}} did not finish in time', { action })}`, kind: 'err' });
      else if (!result.ok) addLog({ text: `✖ [${now()}] ${t('servers.lgsmFailed', '{{action}} failed (exit code {{code}})', { action, code: result.code })}`, kind: 'err' });
      else addLog({ text: `${result.warning ? '⚠' : '✔'} [${now()}] ${t('servers.lgsmDone', '{{action}} finished', { action })}`, kind: result.warning ? 'warn' : 'ok' });
    } catch (err) {
      if (!ctl.signal.aborted) addLog({ text: `✖ [${now()}] ${err.message}`, kind: 'err' });
    } finally {
      setRunning(false);
      onDone && onDone();
    }
  }, [server.id, server.linuxgsm_script, addLog, onDone, t]);

  // Open the live console with the log tab (and run the requested action)
  const started = useRef(false);
  useEffect(() => {
    if (!hasLog || tab !== 'log' || started.current) return;
    started.current = true;
    startStream();
    if (lgsmAction) runAction(lgsmAction);
  }, [hasLog, tab, lgsmAction, startStream, runAction]);

  // Closing the window ends the live console (an action keeps running on the server)
  useEffect(() => () => {
    if (streamAbort.current) streamAbort.current.abort();
    if (actionAbort.current) actionAbort.current.abort();
  }, []);

  const send = async (text) => {
    const c = (text ?? cmd).trim();
    if (!c || sending) return;
    setSending(true);
    setHistory(h => [c, ...h.filter(x => x !== c)].slice(0, 50));
    setHIdx(-1);
    setCmd('');
    setLines(l => [...l, { type: 'cmd', text: `[${now()}] > ${c}` }]);
    try {
      const res = await axios.post(`/api/servers/${server.id}/rcon`, { command: c });
      setLines(l => [...l, { type: 'out', text: res.data.response || '(no output)' }]);
    } catch (err) {
      setLines(l => [...l, { type: 'err', text: err.response?.data?.error || err.message }]);
    } finally {
      setSending(false);
      inputRef.current && inputRef.current.focus();
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); send(); }
    else if (e.key === 'ArrowUp' && history.length) {
      e.preventDefault();
      const i = Math.min(hIdx + 1, history.length - 1); setHIdx(i); setCmd(history[i]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (hIdx > 0) { const i = hIdx - 1; setHIdx(i); setCmd(history[i]); } else { setHIdx(-1); setCmd(''); }
    }
  };

  const copyOut = () => {
    const text = (tab === 'rcon' ? lines : log).map(l => l.text).join('\n');
    navigator.clipboard.writeText(text).catch(() => { });
  };
  const lineCount = tab === 'rcon' ? lines.length : log.length;
  const streamLabel = {
    connecting: t('servers.consoleConnectingShort', 'Connecting…'),
    ended: t('servers.consoleEnded', 'Disconnected'),
    error: t('servers.consoleError', 'Connection error'),
    off: t('servers.consolePaused', 'Paused'),
  }[stream];

  return (
    <div className="sv-console-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sv-console" role="dialog" aria-modal="true">
        <div className="sv-console-head">
          <SvIcon name="terminal" size={17} style={{ color: 'var(--sv-blue-soft)', flexShrink: 0 }} />
          <span className="name">{server.name}</span>
          <span className="addr">{shownAddress(server)}</span>
          {tab === 'log' && stream === 'live' && <span className="sv-live"><i />LIVE</span>}
          {tab === 'log' && running && <span className="sv-console-state"><span className="sv-spin" />{t('servers.lgsmRunning', 'Command running…')}</span>}
          <span className="sv-spacer" />
          <button type="button" className="sv-icon-btn" onClick={onClose} title={t('common.close', 'Close')}><SvIcon name="close" /></button>
        </div>

        <div className="sv-console-tools">
          {hasLog && (
            <div className="sv-seg">
              <button type="button" className={tab === 'rcon' ? 'on' : ''} onClick={() => setTab('rcon')}>RCON</button>
              <button type="button" className={tab === 'log' ? 'on' : ''} onClick={() => setTab('log')}>{t('servers.consoleLog', 'Server log')}</button>
            </div>
          )}
          {tab === 'rcon' ? QUICK_COMMANDS.map(c => (
            <button key={c} type="button" className="sv-btn small" onClick={() => send(c)} disabled={sending}>{c}</button>
          )) : (<>
            {stream === 'live' || stream === 'connecting' ? (
              <button type="button" className="sv-btn small" onClick={stopStream}>
                <SvIcon name="pause" size={12} />{t('servers.consolePause', 'Pause')}
              </button>
            ) : (
              <button type="button" className="sv-btn small success" onClick={startStream}>
                <SvIcon name={stream === 'off' ? 'play' : 'refresh'} size={12} />{stream === 'off' ? t('servers.consoleResume', 'Resume') : t('servers.consoleReconnect', 'Reconnect')}
              </button>
            )}
            {streamLabel && <span className={`sv-console-state${stream === 'error' ? ' err' : ''}`}>{stream === 'connecting' && <span className="sv-spin" />}{streamLabel}</span>}
          </>)}
          <span className="sv-spacer" />
          <button type="button" className="sv-btn small" onClick={copyOut} disabled={!lineCount}><SvIcon name="copy" size={12} />{t('servers.consoleCopy', 'Copy')}</button>
          {tab === 'rcon' && <button type="button" className="sv-btn small" onClick={() => setLines([])} disabled={!lines.length}>{t('common.clear', 'Clear')}</button>}
        </div>

        <pre className="sv-console-out" ref={outRef} onScroll={onOutScroll}>
          {tab === 'rcon' ? (
            lines.length ? lines.map((l, i) => <span key={i} className={l.type === 'cmd' ? 'cmd' : l.type === 'err' ? 'err' : ''}>{l.text}{'\n'}</span>)
              : <span className="dim">{t('servers.consoleHint', 'Type an RCON command below and press Enter. ↑ / ↓ browse your history.')}</span>
          ) : (
            log.length ? log.map((l, i) => <span key={i} className={l.kind === 'log' ? '' : l.kind}>{l.text}{'\n'}</span>)
              : <span className="dim">{stream === 'connecting' ? t('servers.consoleConnecting', 'Connecting over SSH and fetching the console log…') : t('servers.consoleEmpty', '(no output yet)')}</span>
          )}
        </pre>

        {tab === 'rcon' && (
          <div className="sv-console-in">
            <span>{'>'}</span>
            <input ref={inputRef} value={cmd} onChange={e => { setCmd(e.target.value); setHIdx(-1); }} onKeyDown={onKey} autoFocus
              placeholder={t('servers.consolePlaceholder', 'RCON command… (Enter to send)')} />
            <button type="button" className="sv-btn small" onClick={() => send()} disabled={!cmd.trim() || sending}>
              {sending ? <span className="sv-spin" /> : <SvIcon name="send" size={12} />}{t('servers.consoleSend', 'Send')}
            </button>
          </div>
        )}

        <div className="sv-console-foot">
          <span>{tab === 'rcon' ? t('servers.consoleFootRcon', 'Commands run on the server via RCON') : `${t('servers.consoleFootLog', 'Console log via SSH (LinuxGSM)')}${logFile ? ` · ${logFile}` : ''}`}</span>
          <span>{t('servers.consoleLines', '{{count}} lines', { count: lineCount })}{tab === 'log' && stream === 'live' ? ` · ${t('servers.consoleLiveFoot', 'live')}` : ''}</span>
        </div>
      </div>
    </div>
  );
}
